import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSpotify } from '../context/SpotifyContext';
import { Box, Text, Spinner, Center, Heading } from '@chakra-ui/react';
import { supabase } from '../lib/supabaseClient';

const Callback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleCallback } = useSpotify();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [authType, setAuthType] = useState<'spotify' | 'supabase' | 'otp' | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      console.log('Callback - Handling auth callback', {
        url: window.location.href,
        hasHash: !!window.location.hash,
        search: window.location.search
      });

      try {
        // Check if this is an OTP verification
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');
        
        if (tokenParam) {
          setAuthType('otp');
          console.log('Callback - Detected OTP verification');
          
          // Verify OTP token
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenParam,
            type: 'email'
          });
          
          if (error) {
            console.error('OTP verification error:', error);
            setError(`OTP verification error: ${error.message}`);
            setTimeout(() => navigate('/signup'), 3000);
            return;
          }
          
          console.log('OTP verification successful');
          setIsProcessing(false);
          
          // Redirect to intended page or home
          const redirectTo = urlParams.get('redirectTo') || '/';
          setTimeout(() => navigate(redirectTo), 1500);
          return;
        }
        
        // Check if this is a Supabase auth callback (has a hash fragment)
        if (window.location.hash && window.location.hash.includes('access_token')) {
          setAuthType('supabase');
          console.log('Callback - Detected Supabase auth callback (hash with access_token)');
          
          // Supabase will automatically handle the hash fragment
          const { data, error } = await supabase.auth.getSession();
          console.log('Callback - Supabase session result:', { hasSession: !!data.session, error });
          
          if (error) {
            console.error('Supabase auth error:', error);
            setError(`Authentication error: ${error.message}`);
            setTimeout(() => navigate('/'), 3000);
            return;
          }
          
          // Get intended destination from state or default to home
          const from = location.state?.from?.pathname || '/';
          console.log('Callback - Auth successful, redirecting to:', from);
          
          setIsProcessing(false);
          setTimeout(() => navigate(from), 1500);
          return;
        }
        
        // Otherwise, assume it's a Spotify callback
        setAuthType('spotify');
        console.log('Callback - Assuming Spotify callback');
        
        // Check if there's an error parameter in the URL
        const errorParam = urlParams.get('error');
        
        if (errorParam) {
          console.error('Error from Spotify:', errorParam);
          setError(`Authentication error: ${errorParam}`);
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        console.log('Callback - Processing Spotify callback');
        const result = await handleCallback();
        console.log('Callback - Spotify callback result:', { success: !!result });
        

        setIsProcessing(false);
        console.log('Callback - Spotify auth successful, redirecting to player');
        setTimeout(() => navigate('/player'), 1500);
      } catch (err) {
        console.error('Error in callback handling:', err);
        setError('An unexpected error occurred');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleAuthCallback();
  }, [handleCallback, navigate, location]);

  if (error) {
    return (
      <Center h="100vh">
        <Box textAlign="center" p={5}>
          <Text color="red.500" mb={4}>{error}</Text>
          <Text>Redirecting to home page...</Text>
        </Box>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Box textAlign="center">
        {isProcessing ? (
          <>
            <Spinner size="xl" mb={4} />
            <Text>
              {authType === 'spotify' 
                ? 'Authenticating with Spotify...' 
                : authType === 'otp'
                ? 'Verifying your code...'
                : 'Authenticating your account...'}
            </Text>
          </>
        ) : (
          <>
            <Heading size="md" mb={4} color="green.500">
              {authType === 'otp' ? 'Verification successful!' : 'Authentication successful!'}
            </Heading>
            <Text>
              {authType === 'spotify' 
                ? 'Redirecting to the player...' 
                : 'Redirecting...'}
            </Text>
          </>
        )}
      </Box>
    </Center>
  );
};

export default Callback; 